import {useCallback, useEffect, useRef, useState} from 'react';
import {LiveSessionSummary, liveSessionsApi} from '../../../../services/liveSessionsApi';
import {isRequestCanceled} from '../utils';

const LIVE_SESSION_FEED_POLL_INTERVAL_MS = 15000;

const LIVE_SESSION_STATUS_ORDER: Record<LiveSessionSummary['status'], number> = {
  live: 0,
  starting: 1,
  ended: 2,
};

const sortSessions = (sessions: LiveSessionSummary[]) => [...sessions].sort((left, right) => {
  const statusDifference = LIVE_SESSION_STATUS_ORDER[left.status] - LIVE_SESSION_STATUS_ORDER[right.status];

  if (statusDifference !== 0) {
    return statusDifference;
  }

  const leftTimestamp = Date.parse(left.startedAt || left.createdAt);
  const rightTimestamp = Date.parse(right.startedAt || right.createdAt);

  return rightTimestamp - leftTimestamp;
});

const preserveExistingPreview = (
  nextSession: LiveSessionSummary,
  existingSession?: LiveSessionSummary
) => {
  if (nextSession.previewImageUrl || !existingSession?.previewImageUrl) {
    return nextSession;
  }

  return {
    ...nextSession,
    previewImageUrl: existingSession.previewImageUrl,
    previewCapturedAt: existingSession.previewCapturedAt || nextSession.previewCapturedAt,
  };
};

export const useHomeLiveSessions = (token: string | null) => {
  const liveSessionsRequestControllerRef = useRef<AbortController | null>(null);
  const backgroundLiveSessionsRequestControllerRef = useRef<AbortController | null>(null);

  const [liveSessions, setLiveSessions] = useState<LiveSessionSummary[]>([]);
  const [liveSessionsLoading, setLiveSessionsLoading] = useState(true);
  const [liveSessionsError, setLiveSessionsError] = useState<string | null>(null);

  const fetchLiveSessions = useCallback(async (signal?: AbortSignal, background: boolean = false) => {
    if (!token) {
      setLiveSessions([]);
      setLiveSessionsLoading(false);
      setLiveSessionsError(null);
      return;
    }

    if (!signal) {
      if (background) {
        if (liveSessionsRequestControllerRef.current) {
          return;
        }

        backgroundLiveSessionsRequestControllerRef.current?.abort();
        backgroundLiveSessionsRequestControllerRef.current = new AbortController();
      } else {
        backgroundLiveSessionsRequestControllerRef.current?.abort();
        liveSessionsRequestControllerRef.current?.abort();
        liveSessionsRequestControllerRef.current = new AbortController();
      }
    }

    const requestSignal = signal || (
      background
        ? backgroundLiveSessionsRequestControllerRef.current?.signal
        : liveSessionsRequestControllerRef.current?.signal
    );

    try {
      if (!background) {
        setLiveSessionsLoading(true);
        setLiveSessionsError(null);
      }

      const response = await liveSessionsApi.getFeed(token, requestSignal);

      if (requestSignal?.aborted) {
        return;
      }

      setLiveSessions((currentSessions) => {
        const currentSessionsById = new Map(
          currentSessions.map((currentSession) => [currentSession.id, currentSession])
        );

        return sortSessions(response.sessions.map((session) => (
          preserveExistingPreview(session, currentSessionsById.get(session.id))
        )));
      });
    } catch (error) {
      if (isRequestCanceled(error)) {
        return;
      }

      if (background) {
        return;
      }

      setLiveSessionsError(error instanceof Error ? error.message : 'Failed to load live sessions');
    } finally {
      if (!background && !requestSignal?.aborted) {
        setLiveSessionsLoading(false);
      }

      if (!signal) {
        if (background) {
          backgroundLiveSessionsRequestControllerRef.current = null;
        } else {
          liveSessionsRequestControllerRef.current = null;
        }
      }
    }
  }, [token]);

  const upsertLiveSession = useCallback((session: LiveSessionSummary) => {
    setLiveSessions((currentSessions) => {
      const existingSession = currentSessions.find((currentSession) => currentSession.id === session.id);
      const nextSession = preserveExistingPreview(session, existingSession);
      const sessionsWithoutMatch = currentSessions.filter((currentSession) => currentSession.id !== session.id);
      return sortSessions([nextSession, ...sessionsWithoutMatch]);
    });
  }, []);

  const removeLiveSession = useCallback((sessionId: string) => {
    setLiveSessions((currentSessions) => currentSessions.filter((session) => session.id !== sessionId));
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void fetchLiveSessions(controller.signal);

    return () => {
      controller.abort();
      liveSessionsRequestControllerRef.current?.abort();
      backgroundLiveSessionsRequestControllerRef.current?.abort();
    };
  }, [fetchLiveSessions]);

  useEffect(() => {
    if (!token) {
      return;
    }

    const pollIntervalId = window.setInterval(() => {
      void fetchLiveSessions(undefined, true);
    }, LIVE_SESSION_FEED_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(pollIntervalId);
    };
  }, [fetchLiveSessions, token]);

  return {
    liveSessions,
    liveSessionsLoading,
    liveSessionsError,
    fetchLiveSessions,
    upsertLiveSession,
    removeLiveSession,
  };
};
